/**
 * CC4-a (GL216-5, 29 oktober 2026): de naleesstap van een herschreven Word-document.
 *
 * Besluit Frank 24 oktober (advies overgenomen): het model mag een document herschrijven,
 * maar het verschil staat eerst op glas, en pas na Bevestig komt er een bestand. Per alinea
 * het oude (doorgestreept) en het nieuwe; een getal dat het model erbij verzon, wordt bij die
 * alinea gemeld. Bevestig en Weiger gaan naar `POST .../messages/{id}/herschrijving`; het
 * antwoord is de nieuwe lijst bijlagen, met het bestand erachter.
 */
import { useState } from "react";
import axios from "axios";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** De melding bij een mislukt besluit. PUUR. */
export function besluitFout(status, t) {
  if (status === 409) return t("Over dit voorstel is al besloten.");
  if (status === 410) return t("Het originele document staat niet meer bij de vraag.");
  return t("Het besluit is niet doorgekomen. Er is niets gemaakt.");
}

export default function HerschrijfVoorstel({ voorstel, message, onBijlagen }) {
  const { t } = useTaal();
  const [bezig, setBezig] = useState("");
  const [fout, setFout] = useState("");
  const paren = Array.isArray(voorstel?.paren) ? voorstel.paren : [];

  const besluit = async (keuze) => {
    setBezig(keuze);
    setFout("");
    try {
      const r = await axios.post(
        `${API}/conversations/${message.conversation_id}/messages/${message.id}/herschrijving`,
        { besluit: keuze });
      onBijlagen(r.data.attachments);
    } catch (e) {
      setFout(besluitFout(e?.response?.status, t));
    } finally {
      setBezig("");
    }
  };

  return (
    <div className="rounded-md border border-border p-2 space-y-2 text-sm" data-testid="herschrijf-voorstel">
      {paren.map((p, i) => (
        <div key={i} className="space-y-0.5" data-testid="herschrijf-alinea">
          {/* GL220-13: bij een PowerPoint staat de dia erbij ("dia 1"). */}
          {p.plek && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="herschrijf-plek">
              {t("Dia {nr}", { nr: String(p.plek).replace(/^dia\s*/i, "") })}
            </p>
          )}
          <p className="text-muted-foreground line-through" data-testid="herschrijf-oud">{p.oud}</p>
          <p data-testid="herschrijf-nieuw">{p.nieuw}</p>
          {Array.isArray(p.nieuwe_getallen) && p.nieuwe_getallen.length > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400" role="note" data-testid="herschrijf-getallen">
              {t("Nieuw getal, kijk het na: {getallen}", { getallen: p.nieuwe_getallen.join(", ") })}
            </p>
          )}
        </div>
      ))}
      {voorstel?.status === "open" ? (
        <div className="flex flex-wrap gap-1">
          <Button type="button" size="sm" className="h-7 px-2 text-xs" disabled={Boolean(bezig)}
                  onClick={() => besluit("bevestig")} data-testid="herschrijf-bevestig">
            {bezig === "bevestig"
              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden="true" />
              : <Check className="h-3 w-3 mr-1" aria-hidden="true" />}
            {t("Bevestig")}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={Boolean(bezig)}
                  onClick={() => besluit("weiger")} data-testid="herschrijf-weiger">
            <X className="h-3 w-3 mr-1" aria-hidden="true" />
            {t("Weiger")}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="herschrijf-besloten">
          {voorstel?.status === "bevestigd"
            ? t("Bevestigd: het herschreven bestand staat hieronder, naast het origineel.")
            : t("Geweigerd: er is niets gemaakt.")}
        </p>
      )}
      {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
    </div>
  );
}
